import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { jwtDecode as jwt_decode } from 'jwt-decode';
import { Refresh } from '@/apis/auth';
import {
  isPreviewTabActive,
  setPreviewSession,
  setRealSession,
  TOKEN_KEY,
} from '@/common/helper/tokenStorage';
import { clearAllStorageAndState } from '@/common/helper/storageCleanup';

export interface RefreshContextType {
  startRefresh: (token: string) => void;
}

interface RefreshResponse {
  data: {
    access: string;
    refresh: string;
    [key: string]: unknown;
  };
}

interface DecodedToken {
  exp: number;
  [key: string]: unknown; // other fields
}

const defaultRefreshContext: RefreshContextType = {
  startRefresh: () => {},
};

const RefreshTokenContext = createContext<RefreshContextType | undefined>(
  undefined
);

export const useRefreshToken = (): RefreshContextType => {
  const context = useContext(RefreshTokenContext);
  if (!context) {
    return defaultRefreshContext;
  }
  return context;
};

const REFRESH_THRESHOLD = 2 * 60 * 1000; // 2 minutes

export const RefreshTokenProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startRefresh = useCallback((token: string) => {
    if (!token) return;

    // Clear previous timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    let decoded: DecodedToken;
    try {
      decoded = jwt_decode(token);
    } catch {
      return;
    }
    if (!decoded?.exp) return;

    const expTimeMs = decoded.exp * 1000;
    const now = Date.now();
    const timeUntilExpiry = expTimeMs - now;

    if (timeUntilExpiry <= REFRESH_THRESHOLD) {
      // Token is about to expire → refresh immediately
      refreshToken();
    } else {
      // Schedule refresh 2 minutes before expiry
      const refreshIn = timeUntilExpiry - REFRESH_THRESHOLD;
      timeoutRef.current = setTimeout(refreshToken, refreshIn);
    }

    async function refreshToken() {
      try {
        const res: RefreshResponse = await Refresh();
        const newToken = res?.data?.access;
        const refreshToken = res?.data?.refresh;
        if (newToken) {
          // Write back to whichever storage this tab's session is
          // currently using -- sessionStorage while previewing, so the
          // refresh never overwrites the admin's real localStorage token.
          if (isPreviewTabActive()) {
            setPreviewSession(newToken, refreshToken);
          } else {
            setRealSession(newToken, refreshToken);
          }
          startRefresh(newToken); // reschedule next refresh
        } else {
          throw new Error('No token returned');
        }
      } catch (err) {
        console.error('Token refresh failed', err);
        // If refresh token fails (whether preview or regular session), the underlying
        // session is expired/invalid. We must clear both sessionStorage and localStorage
        // via clearAllStorageAndState to prevent falling back to an unrefreshed, stale
        // localStorage token that triggers infinite bounce loops at /login.
        clearAllStorageAndState({ broadcast: true, navigateToLogin: true });
      }
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If another tab refreshed the real token in localStorage,
      // reschedule this tab's timer to avoid duplicate refresh calls.
      if (e.key === TOKEN_KEY && e.newValue && !isPreviewTabActive()) {
        startRefresh(e.newValue);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', handleStorageChange);
      }
    };
  }, [startRefresh]);

  return (
    <RefreshTokenContext.Provider value={{ startRefresh }}>
      {children}
    </RefreshTokenContext.Provider>
  );
};
